import { ApiClient } from './ApiClient';
import { ApiError } from './ApiError';

interface ShopifyAppBridgeGlobal {
  idToken: () => Promise<string>;
}

declare global {
  interface Window {
    shopify?: ShopifyAppBridgeGlobal;
  }
}

const APP_BRIDGE_READY_TIMEOUT_MS = 5000;
const APP_BRIDGE_POLL_INTERVAL_MS = 50;

async function waitForAppBridge(): Promise<ShopifyAppBridgeGlobal> {
  if (window.shopify?.idToken) return window.shopify;
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const interval = window.setInterval(() => {
      if (window.shopify?.idToken) {
        window.clearInterval(interval);
        resolve(window.shopify);
      } else if (Date.now() - start > APP_BRIDGE_READY_TIMEOUT_MS) {
        window.clearInterval(interval);
        reject(new Error('App Bridge did not initialise within timeout'));
      }
    }, APP_BRIDGE_POLL_INTERVAL_MS);
  });
}

export interface AppBridgeApiClientOptions {
  baseUrl?: string;
}

export class AppBridgeApiClient implements ApiClient {
  private readonly baseUrl: string;

  constructor(opts: AppBridgeApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? '';
  }

  get<T>(path: string, opts?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, opts);
  }

  post<T>(path: string, body?: unknown, opts?: RequestInit): Promise<T> {
    return this.request<T>('POST', path, body, opts);
  }

  put<T>(path: string, body?: unknown, opts?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, opts);
  }

  delete<T>(path: string, opts?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, opts);
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    opts?: RequestInit,
  ): Promise<T> {
    const sdk = await waitForAppBridge();
    const token = await sdk.idToken();

    const headers = new Headers(opts?.headers);
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${token}`);
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    const response = await fetch(url, {
      ...opts,
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const parsed = text.length > 0 ? safeJsonParse(text) : undefined;

    if (!response.ok) {
      const message =
        (typeof parsed === 'object' && parsed !== null && 'error' in parsed
          ? String((parsed as Record<string, unknown>).error)
          : response.statusText) || 'Request failed';
      throw new ApiError(response.status, message, parsed);
    }

    return parsed as T;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
